import React, { FC, useCallback, useState, useEffect, useReducer } from 'react';
import classNames from 'classnames';

import { SegmentedInput } from 'components/UI/SegmentedInput';
import { SpinnerWithLabel } from 'components/Spinner';
import { timeoutPromise } from 'utils/helpers';

import { requestGiftCodeDiscount } from 'features/giftCodeDiscounts/api';

const CODE_LENGTH = 8;

type Props = {
  onValid: (code: string) => void;
};

enum ActionType {
  updateCode = 'UPDATE_CODE',
  submitCode = 'SUBMIT_CODE',
  submitCodeSuccess = 'SUBMIT_CODE_SUCCESS',
  submitCodeError = 'SUBMIT_CODE_ERROR',
}

type Action = {
  type: ActionType;
  payload: string;
};

interface State {
  formValid: boolean | undefined;
  lengthValid: boolean | undefined;
  verifying: boolean;
  code: string;
}

const initialState: State = {
  formValid: undefined,
  lengthValid: undefined,
  verifying: false,
  code: '',
};

function reducer(state: State, action: Action) {
  switch (action.type) {
    case ActionType.updateCode:
      return {
        ...state,
        formValid: undefined,
        lengthValid: action.payload?.length === CODE_LENGTH,
        code: action.payload,
      };
    case ActionType.submitCode:
      return {
        ...state,
        verifying: true,
      };
    case ActionType.submitCodeSuccess:
      return {
        ...state,
        verifying: false,
        formValid: true,
      };
    case ActionType.submitCodeError:
      return {
        ...state,
        verifying: false,
        formValid: false,
      };
    default:
      return initialState;
  }
}

export const VoucherInputSection: FC<Props> = ({ onValid }: Props) => {
  const [state, dispatch] = useReducer(reducer, initialState);

  const updateCode = useCallback(
    (code) => dispatch({ type: ActionType.updateCode, payload: code }),
    [dispatch],
  );
  const submitCode = useCallback(
    () => dispatch({ type: ActionType.submitCode, payload: '' }),
    [dispatch],
  );
  const submitCodeSuccess = useCallback(
    () => dispatch({ type: ActionType.submitCodeSuccess, payload: '' }),
    [dispatch],
  );
  const submitCodeError = useCallback(
    () => dispatch({ type: ActionType.submitCodeError, payload: '' }),
    [dispatch],
  );

  const handleOnSubmit = useCallback(() => {
    if (state.code.length !== CODE_LENGTH) {
      return submitCodeError();
    }
    return submitCode();
  }, [state.code]);

  useEffect(() => {
    if (state.lengthValid && state.verifying) {
      Promise.all([requestGiftCodeDiscount(state.code), timeoutPromise(2000)])
        .then(() => {
          submitCodeSuccess();
        })
        .catch(() => {
          submitCodeError();
        });
    }
  }, [state.verifying]);

  useEffect(() => {
    if (state.lengthValid) {
      handleOnSubmit();
    }
  }, [state.code]);

  useEffect(() => {
    state.formValid && onValid(state.code);
  }, [state.formValid, state.code, onValid]);

  return (
    <div className='text-sm-center px-1'>
      <h1 className='standfirst standfirst--lead lh-tight mb-2 mb-lg-3'>
        LG and Sedition bring an art gallery experience to your home
      </h1>
      <p className='fs-16 mb-1'>Enter your voucher code to claim a free subscription</p>
      <SegmentedInput
        fields={CODE_LENGTH}
        groupsSize={4}
        className={classNames('mb-1 segmented-input--groups-of-four', {
          'animate-shake': state.formValid === false && state.verifying,
        })}
        inputStyle={{
          color:
            state.formValid === false
              ? '#c20e1a'
              : state.formValid === true
              ? '#00C49D'
              : undefined,
        }}
        disabled={state.verifying}
        onChange={updateCode}
      />
      <div className='d-flex justify-content-between align-items-center'>
        {state.verifying && (
          <SpinnerWithLabel size='s' light>
            Verifying...
          </SpinnerWithLabel>
        )}
        {state.formValid === false && !state.verifying && (
          <div className='key-value key-value__value color-error'>Code invalid</div>
        )}
        <div className='flex-grow' />
        <button className='btn btn--border' onClick={handleOnSubmit} disabled={state.verifying}>
          Next
        </button>
      </div>
    </div>
  );
};
